'use client';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Image from '@repo/ui/src/components/Image';
import ShopButton from '@views/RecipeDetailPage/_ShopButton';
import SaveButton from '@views/RecipeDetailPage/_SaveButton';
import PrintButton from '@views/RecipeDetailPage/_PrintButton';
import RecipeCardInfo from '@views/RecipeDetailPage/_RecipeCardInfo';
import ShareIcon from '@repo/ui/src/components/Icons/ShareIcon';
import Tooltip from '@components/Tooltip';
import RecipeDescription from './_RecipeDescription';
import RatingStars from '@repo/ui/src/components/RatingStars';
import { recipeShared } from '@repo/ui/src/services/analytics';
import { SegmentRecipeProps } from '@components/RecipeNotes/_RecipeNote';
import {
  IngredientSummaryProps,
  MetaProps,
  RecipeMetaSectionProps,
  RecipeMigrationProps,
  RecipeStepProps,
} from '@repo/ui/src/types';

export interface RecipeTimeProps {
  label: string;
  value: number;
}
interface Props {
  heroImage: string | null;
  recipeMetaSection: RecipeMetaSectionProps;
  recipeMigrationSection: RecipeMigrationProps;
  ratingSummary: {
    average: number;
    count: number;
  };
  servingSize: number;
  authorName: string;
  _meta: MetaProps;
  list?: Array<any>;
  prepTime: Array<RecipeTimeProps>;
  cookTime: Array<RecipeTimeProps>;
  steps?: Array<RecipeStepProps>;
  ingredientSummary?: IngredientSummaryProps;
  unitOfMeasure: number;
  // setTabIndex: (value: number) => void;
  _testID: string;
  segmentRecipe: SegmentRecipeProps;
}

const RecipeHero = ({
  heroImage,
  recipeMetaSection,
  recipeMigrationSection,
  ratingSummary,
  servingSize,
  authorName,
  list,
  prepTime,
  cookTime,
  steps,
  ingredientSummary,
  unitOfMeasure,
  // setTabIndex,
  _testID,
  _meta,
  segmentRecipe,
}: Props) => {
  const [tooltipShow, setTooltipShow] = React.useState('');
  const ref = React.useRef<number>();
  const { t } = useTranslation();
  const url = `https://traeger.com/${_meta?.deliveryKey}`;

  const openTooltip = React.useCallback(
    (text: string) => {
      setTooltipShow(text);
      window.clearTimeout(ref.current);
      ref.current = window.setTimeout(() => {
        setTooltipShow('');
      }, 2000);
    },
    [ref],
  );

  const share = React.useCallback(() => {
    openTooltip(t('recipeContent.linkCopied', undefined, { default: 'Link Copied' }));
    recipeShared({
      ...segmentRecipe,
    });
  }, [openTooltip, t]);

  return (
    <div className="md:tw-grid md:tw-grid-cols-[5fr_4fr] tw-gap-6 lg:tw-gap-36 sm:tw-pt-[82px]">
      <div className="tw-relative tw-aspect-video tw-w-full md:tw-aspect-auto md:tw-h-[525px] md:tw-mt-6">
        <div className="tw-absolute tw-pt-4 tw-z-10 tw-right-6 sm:tw-hidden">
          <CopyToClipboard text={url} onCopy={share}>
            <button className="tw-flex tw-justify-center tw-relative">
              <ShareIcon className="tw-cursor-pointer tw-flex" color="#ffff" />
              <Tooltip
                visible={!!tooltipShow}
                className="tw-bg-surface-700 tw-text-text-100 after:tw-border-b-surface-700"
                below
              >
                {tooltipShow}
              </Tooltip>
            </button>
          </CopyToClipboard>
        </div>
        {heroImage && (
          <Image src={heroImage} alt={recipeMetaSection?.title} objectFit="cover" layout="fill" />
        )}
      </div>
      <div className="tw-w-full tw-px-6 sm:tw-px-0 sm:tw-w-auto">
        <div>
          <div className="tw-hidden sm:tw-flex tw-gap-2">
            <div className="tw-relative tw-pt-5 md:tw-pt-0">
              <CopyToClipboard text={url} onCopy={share}>
                <button className="tw-flex tw-justify-center tw-relative tw-bg-surface-300 tw-rounded-full hover:tw-bg-action-400 hover:tw-text-surface-100 focus:tw-text-action-500">
                  <ShareIcon className="tw-cursor-pointer tw-flex tw-w-10" />
                  <Tooltip
                    visible={!!tooltipShow}
                    className="tw-bg-surface-700 after:tw-border-b-surface-700 tw-text-text-100"
                    below
                  >
                    {tooltipShow}
                  </Tooltip>
                </button>
              </CopyToClipboard>
            </div>
            <PrintButton
              variant="icon"
              servingSize={servingSize}
              authorName={authorName}
              ingredientSummary={ingredientSummary}
              unitOfMeasure={unitOfMeasure}
              recipeMetaSection={recipeMetaSection}
              prepTime={prepTime}
              cookTime={cookTime}
              steps={steps}
              segmentRecipe={segmentRecipe}
            />
          </div>
          <hr className="tw-h-1 tw-w-12 tw-bg-action-400 tw-hidden sm:tw-block tw-my-6" />
          <h1 className="tra-text-web-heading-3 tw-font-normal tw-pt-6 md:tw-pt-0">
            {recipeMetaSection?.title}
          </h1>
        </div>
        <p className="tra-text-web-body-s tw-pt-2 sm:tw-pt-4">By {authorName}</p>
        <div className="tw-flex tw-row tw-mt-4">
          {!!ratingSummary?.average && recipeMigrationSection ? (
            <div className="tw-pb-4">
              <RatingStars value={ratingSummary.average} reviewCount={ratingSummary.count} />
            </div>
          ) : null}
        </div>
        {!!recipeMetaSection?.description && (
          <RecipeDescription description={recipeMetaSection?.description} />
        )}
        <div className="tw-pb-10 tw-flex tw-mt-4 tw-flex-wrap tw-gap-4 sm:tw-gap-2 lg:tw-gap-4">
          {!!list?.length && (
            <ShopButton
              className="tw-basis-auto tw-min-w-48 tw-flex-grow lg:tw-flex-1"
              data={list}
            />
          )}
          {!!_meta.deliveryId && (
            <SaveButton
              contentId={_meta.deliveryId}
              className="tw-basis-auto tw-min-w-52 tw-flex-grow lg:tw-flex-1"
            />
          )}
        </div>
        <RecipeCardInfo
          recipeMetaSection={recipeMetaSection}
          _testID={_testID}
          prepTime={prepTime}
          cookTime={cookTime}
        />
      </div>
    </div>
  );
};
export default RecipeHero;
